import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { AuthUser, UserType } from '@/shared/types'

export interface LoginPayload {
  email: string
  password: string
  user_type?: string   // 'ADMIN' | 'SCHOOL_ADMIN' | 'COLLECTOR' | 'ASSISTANT'
}

export interface LoginResponse {
  data: {
    user: Record<string, unknown>
    authorization: { token: string; type: string }
  }
}

// The backend has several admin-level types (SYSTEM_ADMIN, ADMIN, …). The app only
// distinguishes 'school_admin', 'collector', 'assistant' and the full-access 'admin',
// so every other admin-level type (incl. SYSTEM_ADMIN) maps to 'admin'.
export function normalizeUserType(raw: unknown, fallback: UserType = 'admin'): UserType {
  const t = String(raw ?? '').trim().toLowerCase()
  if (!t) return fallback
  if (t === 'school_admin' || t === 'collector' || t === 'assistant') return t
  return 'admin'
}

export const authService = {
  login: async (payload: LoginPayload): Promise<{ user: AuthUser; token: string }> => {
    // Map lowercase user_type to uppercase for API (ADMIN / SCHOOL_ADMIN)
    const body = {
      email: payload.email,
      password: payload.password,
      user_type: payload.user_type?.toUpperCase() ?? 'ADMIN',
    }

    // Collector uses a different endpoint
    const url = body.user_type === 'COLLECTOR'
      ? endpoints.auth.collectorLogin
      : endpoints.auth.login

    const res = await restClient.post<LoginResponse>(url, body)

    // Exact shape from source: res.data.data.authorization.token
    const token: string = res.data?.data?.authorization?.token ?? ''
    const rawUser = res.data?.data?.user ?? {}

    // Persist token to localStorage matching original project key
    localStorage.setItem('token', token)
    localStorage.setItem('user_type', body.user_type)

    const user: AuthUser = {
      id:        (rawUser.id as number) ?? 0,
      name:      (rawUser.name as string) ?? '',
      email:     (rawUser.email as string) ?? payload.email,
      user_type: normalizeUserType(rawUser.user_type, normalizeUserType(payload.user_type)),
      photo_url: (rawUser.photo_url as string | null) ?? null,
      token,
      school_id: rawUser.vendor_id as number | undefined,
      school:    rawUser.vendor as AuthUser['school'],
      roles:              rawUser.roles as AuthUser['roles'],
      school_admin_roles: rawUser.school_admin_roles as AuthUser['school_admin_roles'],
      permissions:        Array.isArray(rawUser.permissions) ? (rawUser.permissions as string[]) : undefined,
      is_super_admin:     typeof rawUser.is_super_admin === 'boolean' ? rawUser.is_super_admin : undefined,
    }

    return { user, token }
  },

  logout: async () => {
    try {
      await restClient.post('/logout')
    } catch {
      // ignore
    }
    localStorage.removeItem('token')
    localStorage.removeItem('user_type')
  },

  me: async (): Promise<AuthUser | null> => {
    const userType = localStorage.getItem('user_type') ?? 'ADMIN'
    const url = userType === 'COLLECTOR' ? endpoints.auth.meCollector : endpoints.auth.me
    try {
      const res = await restClient.get<{ data: Record<string, unknown> }>(url)
      const rawUser = res.data?.data ?? {}
      return {
        id:        (rawUser.id as number) ?? 0,
        name:      (rawUser.name as string) ?? '',
        email:     (rawUser.email as string) ?? '',
        user_type: normalizeUserType(rawUser.user_type, normalizeUserType(userType)),
        photo_url: (rawUser.photo_url as string | null) ?? null,
        token:     localStorage.getItem('token') ?? '',
        school_id: rawUser.vendor_id as number | undefined,
        school:    rawUser.vendor as AuthUser['school'],
        roles:              rawUser.roles as AuthUser['roles'],
        school_admin_roles: rawUser.school_admin_roles as AuthUser['school_admin_roles'],
        permissions:        Array.isArray(rawUser.permissions) ? (rawUser.permissions as string[]) : undefined,
        is_super_admin:     typeof rawUser.is_super_admin === 'boolean' ? rawUser.is_super_admin : undefined,
      }
    } catch {
      return null
    }
  },

  // Every permission KEY the logged-in admin holds — i.e. everything they may grant to a role.
  // The RBAC API only accepts these keys ('GET /admin/…', 'GQL Query.…'), not the legacy
  // module names from admin/permission/get-list, so this is the catalog the role editor uses.
  permissionCatalog: async (): Promise<Array<{ id: number; name: string; key: string; section?: string; kind?: string; method?: string; path?: string }>> => {
    const res = await restClient.get<{ data: Record<string, any> }>(endpoints.auth.me)
    const d = res.data?.data ?? {}
    const details: any[] = Array.isArray(d.permission_details) ? d.permission_details : []
    if (details.length) {
      return details.map((p, i) => ({ id: i + 1, name: p.name ?? p.key, key: String(p.key), section: p.section, kind: p.kind, method: p.method, path: p.path }))
    }
    const keys: string[] = Array.isArray(d.permissions) ? d.permissions : []
    return keys.map((k, i) => ({ id: i + 1, name: k, key: k }))
  },

  forgotPassword: (email: string) =>
    restClient.post(endpoints.auth.forgotPassword, { email }).then(r => r.data),

  verifyOTP: (body: { email: string; otp: string; password: string }) =>
    restClient.post(endpoints.auth.verifyOTP, body).then(r => r.data),
}
